import { tss } from 'tss-react/mui';

import type { ThemeMode } from '@/types/theme';

const useStyles = tss
  .withParams<{
    themeMode: ThemeMode;
  }>()
  .create(({ theme, themeMode }) => ({
    dpTableContainer: {
      maxHeight: 'unset',
      minHeight: 'auto',
      borderRadius: theme.shape.borderRadius
    },
    dpTable: {
      boxShadow: 'none',
      border: `1px solid ${theme.vars.palette.border.light}`
    },
    ingestFromInfoTooltip: {
      backgroundColor: theme.vars.palette.background.navigation
    },
    ingestFromInfoIcon: {
      color: theme.vars.palette.info.main
    },
    dsHeaderTableCell1: {
      width: '100%',
      padding: '.5rem',
      cursor: 'default',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: theme.vars.palette.background.elevation0,
      borderBottom: 'none',
      gap: 1,
      border: 'none',
      py: 1.5
    },
    dsHeaderTableCell2: {
      padding: '.5rem',
      textAlign: 'right',
      backgroundColor: theme.vars.palette.background.elevation0,
      borderBottom: 'none'
    },
    selectedCountText: {
      color: theme.vars.palette.primary.main,
      fontWeight: 500
    },
    CollapseTableRow: {
      borderTop: `1px solid ${theme.vars.palette.border.main}`,
      td: {
        paddingTop: '0rem',
        paddingBottom: '0rem',
        paddingRight: '0rem'
      }
    },
    ciscoToggleButton: {
      '& .MuiToggleButton-root': {
        color: 'gray', // default text color
        borderColor: theme.vars.palette.primary.main,
        padding: '1.2rem', // button height/width
        minWidth: '3rem' // width
      },
      '& .Mui-selected': {
        color: theme.vars.palette.background.elevation2,
        backgroundColor: theme.vars.palette.primary.main, // active color
        borderColor: theme.vars.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.vars.palette.primary.dark
        }
      }
    },
    inputEditable: {
      flexGrow: 1,
      '& fieldset': {
        borderTopLeftRadius: '0px',
        borderBottomLeftRadius: '0px'
      }
    },
    inputDisabled: {
      flexGrow: 1,
      backgroundColor: 'var(--mui-palette-transparent-a5)',
      '& input': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      '& fieldset': {
        borderColor: 'var(--mui-palette-action-disabled)',
        borderTopRightRadius: '0px',
        borderBottomRightRadius: '0px'
      }
    },
    InputGroup: {
      position: 'relative',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'stretch',
      width: '100%',
      backgroundColor: theme.vars.palette.background.elevation2
    },
    dFlex: {
      display: 'flex'
    },
    antSwitchText: {
      color: 'var(--mui-palette-primary-main)'
    },
    authorizeUrlLink: {
      color: theme.vars.palette.primary.main,
      '&:active, &:hover': {
        color: theme.vars.palette.primary.main
      }
    },
    fetchTimeLabel: {
      display: 'flex',
      width: '100%'
    },

    fetchTimeSwitch: {
      marginLeft: 'auto',
      marginRight: 0,
      width: 28,
      height: 16,
      padding: 0,
      display: 'flex',
      '&:active': {
        '& .MuiSwitch-thumb': {
          width: 15
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
          transform: 'translateX(9px)'
        }
      },
      '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
          transform: 'translateX(12px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            opacity: 1
          }
        }
      },
      '& .MuiSwitch-thumb': {
        boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: '#fff',
        transition: theme.transitions.create(['width'], {
          duration: 200
        })
      },
      '& .MuiSwitch-track': {
        borderRadius: 16 / 2,
        opacity: 1,
        backgroundColor: 'var(--mui-palette-error-main)',
        boxSizing: 'border-box'
      },
      '& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
        backgroundColor: theme.vars.palette.primary.main
      }
    },
    modalInfoHeading: {
      color: theme.vars.palette.primary.main,
      marginBottom: '.25rem'
    },
    modalInfoList: {
      marginTop: '0rem',
      marginBottom: '0rem'
    },
    antSwitch: {
      margin: '0 .5rem',
      width: 28,
      height: 16,
      padding: 0,
      display: 'flex',
      '&:active': {
        '& .MuiSwitch-thumb': {
          width: 15
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
          transform: 'translateX(0.563rem)'
        }
      },
      '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
          transform: 'translateX(0.75rem)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            opacity: 1
          }
        }
      },
      '& .MuiSwitch-thumb': {
        boxShadow: '0 0.125rem 0.25rem 0 rgb(0 35 11 / 20%)',
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: 'var(--mui-palette-primary-main)',
        transition: theme.transitions.create(['width'], {
          duration: 200
        })
      },
      '& .MuiSwitch-track': {
        borderRadius: 16 / 2,
        opacity: 1,
        border: '1px solid var(--mui-palette-primary-main)',
        backgroundColor: 'transparent',
        boxSizing: 'border-box'
      },
      fileUploaderDeleteIcon: {
        color: theme.vars.palette.common.white,
        '&:hover': {
          color: theme.vars.palette.text.primary_a75
        }
      },
      fileUploaderFileInput: {
        display: 'none'
      },
      CustomSwitch: {
        marginLeft: '.8rem',
        width: 52,
        height: 26,
        padding: 0,
        '& .MuiSwitch-switchBase': {
          padding: 0,
          margin: 2,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(26px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: theme.vars.palette.primary.main,
              opacity: 1,
              border: 0
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5
            }
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid #fff'
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color:
              themeMode === 'light'
                ? theme.vars.palette.grey[100]
                : theme.vars.palette.grey[600]
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: themeMode === 'light' ? 0.7 : 0.3
          }
        },
        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          width: 22,
          height: 22,
          borderRadius: 8 / 2
        },
        '& .MuiSwitch-track': {
          borderRadius: 8 / 2,
          backgroundColor: 'var(--mui-palette-error-main)',
          opacity: 1,
          transition: theme.transitions.create(['background-color'], {
            duration: 500
          })
        }
      },
      refreshBtn: {
        position: 'absolute',
        right: '0rem',
        height: '2.5rem'
      },
      downloadFileBtn: {
        position: 'absolute',
        right: '2.5rem',
        height: '2.5rem'
      },
      downloadRefreshFile: {
        appearance: 'none',
        backgroundClip: 'padding-box',
        display: 'block',
        lineHeight: '1.5',
        width: '100%',
        input: {
          padding: 0,
          height: 'auto'
        }
      }
    },
    choseFileBtn: {
      position: 'absolute',
      right: '0rem',
      height: '2.5rem'
    },
    widthFull: {
      width: '100%'
    },
    multiSelectForm: {
      '&.MuiAutocomplete-root .MuiOutlinedInput-root': {
        padding: '0.125rem .8rem',
        height: 'auto'
      },
      '& div.MuiAutocomplete-tag svg.MuiChip-deleteIcon': {
        fontSize: '1.125rem',
        marginLeft: '0rem'
      },
      'div.MuiAutocomplete-tag': {
        backgroundColor: theme.vars.palette.background.elevation0
      }
    },
    calanderPadding: {
      '& .MuiInputBase-root.MuiOutlinedInput-root': {
        paddingRight: '0.375rem'
      }
    },
    modalAccordion: {
      color: theme.vars.palette.primary.main,
      border: '0',
      '&:not(:last-child)': {
        borderBottom: 0
      },
      '&::before': {
        display: 'none'
      }
    },
    modalAccordionSummary: {
      backgroundColor: theme.vars.palette.background.elevation2,
      borderBottom: '0rem solid',
      borderColor: 'rgba(var(--mui-palette-common-onBackgroundChannel) / 0.23)',
      flexDirection: 'row',
      minHeight: 'auto',
      '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(-90deg)',
        '& .MuiAccordionSummary-content': {
          margin: '0.5rem'
        }
      }
    },
    modalAccordionDetails: {
      backgroundColor: 'var(--mui-palette-background-paper)',
      padding: theme.spacing(2),

      borderTop: '0.125rem solid',
      borderColor: theme.vars.palette.background.elevation0
    },

    checkEnableForm: {
      margin: 0,
      float: 'right'
    },
    fileUploaderChip: {
      backgroundColor: 'transparent',
      color: theme.vars.palette.primary.light,
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.vars.palette.text.primary
      },
      '&:focus': {
        backgroundColor: 'transparent',
        color: theme.vars.palette.text.primary
      }
    },
    fileUploaderDeleteIcon: {
      color: theme.vars.palette.common.white,
      '&:hover': {
        color: theme.vars.palette.text.primary_a75
      }
    },
    fileUploaderFileInput: {
      display: 'none'
    },
    CustomSwitch: {
      marginLeft: '.8rem',
      width: 52,
      height: 26,
      padding: 0,
      '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
          transform: 'translateX(26px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            backgroundColor: theme.vars.palette.primary.main,
            opacity: 1,
            border: 0
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.5
          }
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
          color: '#33cf4d',
          border: '6px solid #fff'
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
          color:
            themeMode === 'light'
              ? theme.vars.palette.grey[100]
              : theme.vars.palette.grey[600]
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: themeMode === 'light' ? 0.7 : 0.3
        }
      },
      '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
        borderRadius: 8 / 2
      },
      '& .MuiSwitch-track': {
        borderRadius: 8 / 2,
        backgroundColor: 'var(--mui-palette-error-main)',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
          duration: 500
        })
      }
    },
    refreshBtn: {
      position: 'absolute',
      right: '0rem',
      height: '2.5rem'
    },
    downloadFileBtn: {
      position: 'absolute',
      right: '2.5rem',
      height: '2.5rem'
    },
    downloadRefreshFile: {
      appearance: 'none',
      backgroundClip: 'padding-box',
      display: 'block',
      lineHeight: '1.5',
      width: '100%',
      '& .MuiInputBase-formControl': {
        width: '100%',
        input: {
          height: 'auto'
        }
      }
    },
    tab: {
      color: theme.vars.palette.text.secondary,
      [theme.breakpoints.down('md')]: {
        fontSize: '0.75rem',
        minWidth: '3rem'
      }
    },
    ingestDataTab: {
      pl: '.5rem',
      mt: 1,
      border: 1,
      borderColor: 'divider',
      borderTopRightRadius: '.25rem',
      borderTopLeftRadius: '.25rem',
      '& .errorTabText': {
        color: `${theme.vars.palette.error.main} !important`
      },
      '& .errorTabHighlight': {
        color: theme.vars.palette.error.main
      }
    }
  }));

export default useStyles;
